import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InvitePreview, InviteRow, MemberRow, Role } from '@bingo/shared';
import { ShareLink } from '../../components/ShareLink.js';
import { ApiError, api } from '../../lib/api.js';
import { sendCommand } from '../../lib/commands.js';
import { useSession } from '../../lib/useSession.js';

/**
 * /admin/membros — quem participa da organização.
 *
 * O convite por link é o caminho principal: quem administra informa e-mail,
 * nome e papel, e recebe um link para mandar. Não existe senha provisória
 * aqui — ela ficaria no histórico do WhatsApp e quase nunca seria trocada.
 */

interface EventOption {
  id: string;
  name: string;
}

const ROLE_LABEL: Record<Role, string> = {
  OWNER: 'Dono',
  ADMIN: 'Administrador',
  OPERATOR: 'Operador',
};

export function Membros() {
  const session = useSession();
  const isOwner = session.activeRole === 'OWNER';

  const [members, setMembers] = useState<MemberRow[] | null>(null);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [preview, setPreview] = useState<{ preview: InvitePreview; title: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('OPERATOR');
  const [eventIds, setEventIds] = useState<string[]>([]);

  const organizationId = session.activeOrganizationId;

  const reload = useCallback(() => {
    if (!organizationId) return;

    void Promise.all([
      api<MemberRow[]>('/api/admin/members'),
      api<InviteRow[]>('/api/admin/invites'),
      api<EventOption[]>('/api/events'),
    ])
      .then(([m, i, e]) => {
        setMembers(m);
        setInvites(i);
        setEvents(e);
      })
      .catch(() => setMembers([]));
  }, [organizationId]);

  useEffect(reload, [reload]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);

    try {
      await action();
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível completar a ação.');
    } finally {
      setBusy(false);
    }
  };

  const convidar = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;

    await run(async () => {
      const created = await sendCommand<InvitePreview>('/api/admin/invites', {
        body: { email: email.trim(), name: name.trim() || null, role, eventIds },
      });

      setPreview({ preview: created, title: 'Convite criado' });
      setEmail('');
      setName('');
      setEventIds([]);
    });
  };

  return (
    <main className="shell">
      <header className="shell-head">
        <div>
          <h1>Membros</h1>
          <p className="hint-text">Quem participa desta organização.</p>
        </div>
        <Link className="btn" to="/admin">
          Administração
        </Link>
      </header>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {preview ? (
        <ShareLink
          preview={preview.preview}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      ) : null}

      <section>
        <h2>Convidar</h2>

        <form className="form" onSubmit={(event) => void convidar(event)}>
          <label className="field">
            <span>E-mail</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="pessoa@exemplo.com"
              required
            />
          </label>

          <label className="field">
            <span>Nome (opcional)</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como chamar"
            />
          </label>

          <label className="field">
            <span>Papel</span>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="OPERATOR">Operador — conduz o jogo</option>
              {/* Só o dono convida administrador; e ninguém convida dono por
                  link, porque dono novo é decisão deliberada, na tela. */}
              {isOwner ? <option value="ADMIN">Administrador — gerencia tudo</option> : null}
            </select>
          </label>

          {events.length > 0 ? (
            <fieldset className="field">
              <legend>Já pode operar (opcional)</legend>
              <div className="checks">
                {events.map((event) => (
                  <label key={event.id} className="check">
                    <input
                      type="checkbox"
                      checked={eventIds.includes(event.id)}
                      onChange={(e) =>
                        setEventIds((current) =>
                          e.target.checked
                            ? [...current, event.id]
                            : current.filter((id) => id !== event.id),
                        )
                      }
                    />
                    <span>{event.name}</span>
                  </label>
                ))}
              </div>
              <p className="hint-text">
                Sem nenhum marcado, a pessoa entra sem ver eventos — você pode designar depois.
              </p>
            </fieldset>
          ) : null}

          <button type="submit" className="btn primary block" disabled={busy}>
            {busy ? 'Gerando…' : 'Gerar convite'}
          </button>
        </form>
      </section>

      {invites.length > 0 ? (
        <section>
          <h2>Convites pendentes</h2>
          <ul className="people-list">
            {invites.map((invite) => (
              <li key={invite.id} className="person-row">
                <div>
                  <strong>{invite.email}</strong>
                  <small>
                    {ROLE_LABEL[invite.role]}
                    {invite.expired ? ' · vencido' : ` · vence ${whenLabel(invite.expiresAt)}`}
                  </small>
                </div>
                <div className="person-actions">
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const novo = await sendCommand<InvitePreview>(
                          `/api/admin/invites/${invite.id}/resend`,
                        );
                        setPreview({ preview: novo, title: 'Convite reenviado' });
                      })
                    }
                  >
                    Reenviar
                  </button>
                  <button
                    type="button"
                    className="btn danger"
                    disabled={busy}
                    onClick={() =>
                      void run(() => api(`/api/admin/invites/${invite.id}`, { method: 'DELETE' }))
                    }
                  >
                    Revogar
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <p className="hint-text">Reenviar gera um link novo e derruba o anterior.</p>
        </section>
      ) : null}

      <section>
        <h2>Membros</h2>

        {members === null ? (
          <p className="hint-text">Carregando…</p>
        ) : (
          <ul className="people-list">
            {members.map((member) => (
              <li key={member.membershipId} className="person-row">
                <div>
                  <strong>{member.name}</strong>
                  <small>{member.email}</small>
                  {member.lastOwner ? <small>único dono da organização</small> : null}
                </div>

                <div className="person-actions">
                  <label className="field inline">
                    <span className="sr-only">Papel de {member.name}</span>
                    <select
                      value={member.role}
                      disabled={busy || member.lastOwner}
                      onChange={(e) =>
                        void run(() =>
                          api(`/api/admin/members/${member.membershipId}`, {
                            method: 'PATCH',
                            body: { role: e.target.value },
                          }),
                        )
                      }
                    >
                      {isOwner ? <option value="OWNER">Dono</option> : null}
                      {isOwner ? <option value="ADMIN">Administrador</option> : null}
                      <option value="OPERATOR">Operador</option>
                    </select>
                  </label>

                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const link = await sendCommand<InvitePreview>(
                          `/api/admin/members/${member.membershipId}/reset`,
                        );
                        setPreview({ preview: link, title: 'Link para redefinir o acesso' });
                      })
                    }
                  >
                    Redefinir acesso
                  </button>

                  <button
                    type="button"
                    className="btn danger"
                    disabled={busy || member.lastOwner}
                    onClick={() =>
                      void run(() =>
                        api(`/api/admin/members/${member.membershipId}`, { method: 'DELETE' }),
                      )
                    }
                  >
                    Remover
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
